import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, useTheme, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Collapse, Paper, Stack, Chip, Pagination } from "@mui/material";
import { tokens } from "../../../theme";
import superAdminService from "../../../services/SüperAdminServices/superAdminService";
import DataTable from "../../../components/SüperAdminComponents/DataTable";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export default function PasswordResetsPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [resets, setResets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState({
    email: "",
    used: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await superAdminService.getAllPasswordResets({
        withMeta: true,
        params: appliedFilters,
        headers: {
          "x-bone-pageindex": pageIndex,
          "x-bone-limit": pageSize,
          "x-bone-sortby": sortBy,
          "x-bone-sortorder": sortOrder,
        },
      });
      setResets(response.data || []);
      setTotalCount(Number(response.headers?.["x-total-count"] || 0));
      setTotalPages(Number(response.headers?.["x-total-pages"] || 1));
    } catch (error) {
      setSnackbar({ open: true, message: "Error fetching password resets", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, pageIndex, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleView = (item) => {
    setSelectedItem(item);
    setViewDialogOpen(true);
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await superAdminService.deletePasswordReset(selectedItem.id);
      setSnackbar({ open: true, message: "Password reset deleted successfully", severity: "success" });
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: "Error deleting password reset", severity: "error" });
    }
  };

  const columns = [
    { id: "email", label: "Email" },
    { id: "otp", label: "OTP" },
    { id: "used_at", label: "Used", format: (value) => (value ? "Yes" : "No") },
  ];

  const sanitizeFilters = useMemo(() => () => {
    const output = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) output[key] = value;
    });
    return output;
  }, [filters]);

  const applyFilters = () => {
    setPageIndex(0);
    setAppliedFilters(sanitizeFilters());
  };

  const resetFilters = () => {
    setFilters({ email: "", used: "", dateFrom: "", dateTo: "" });
    setPageIndex(0);
    setAppliedFilters({});
  };

  const activeFilterCount = Object.keys(appliedFilters || {}).length;

  const getFilenameFromHeaders = (headers) => {
    const contentDisposition = headers?.["content-disposition"] || "";
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    return match?.[1] || `password-resets-export-${Date.now()}.xlsx`;
  };

  const handleExcelExport = async () => {
    try {
      setExportLoading(true);
      const response = await superAdminService.exportExcel({
        resource: "passwordResets",
        filters: appliedFilters,
        sortBy,
        sortOrder,
      });
      const fileName = getFilenameFromHeaders(response.headers);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "Excel downloaded successfully", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Excel export failed", severity: "error" });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Box sx={{ 
      p: 3, 
      width: "100%", 
      minWidth: "100%", 
      maxWidth: "100%", 
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, width: "100%", minWidth: "100%" }}>
        <VpnKeyIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
        <Typography variant="h2" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.blueAccent[200]} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          Password Resets Management
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<FilterListIcon />}
              endIcon={filtersOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              onClick={() => setFiltersOpen((prev) => !prev)}
              sx={{
                color: colors.grey[100],
                backgroundColor: filtersOpen ? colors.blueAccent[700] : colors.primary[300],
                "&:hover": { backgroundColor: filtersOpen ? colors.blueAccent[800] : colors.primary[200] },
              }}
            >
              Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
            </Button>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExcelExport} disabled={exportLoading}>
              {exportLoading ? "Exporting..." : "Export Excel"}
            </Button>
          </Stack>
        </Box>
      </Box>
      <Collapse in={filtersOpen} timeout={250} unmountOnExit>
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1.5, mb: 1.5 }}>
            <TextField label="Email" size="small" value={filters.email} onChange={(event) => setFilters((prev) => ({ ...prev, email: event.target.value }))} />
            <TextField select label="Used" size="small" value={filters.used} onChange={(event) => setFilters((prev) => ({ ...prev, used: event.target.value }))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>
            <TextField label="Date From" type="date" size="small" value={filters.dateFrom} onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="Date To" type="date" size="small" value={filters.dateTo} onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))} InputLabelProps={{ shrink: true }} />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={applyFilters}>Apply Filters</Button>
            <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>Clear</Button>
          </Stack>
        </Paper>
      </Collapse>
      <DataTable columns={columns} data={resets} onView={handleView} onDelete={handleDelete} loading={loading} emptyMessage="No password resets found" />
      <Paper elevation={0} sx={{ mt: 2, p: 1.5, borderRadius: 2, background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={`Total ${totalCount}`} />
            <Chip size="small" label={`Page ${pageIndex + 1}/${totalPages}`} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField select label="Rows" size="small" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPageIndex(0); }} sx={{ minWidth: 90 }}>
              {[10, 20, 50, 100].map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
            </TextField>
            <TextField select label="Sort By" size="small" value={sortBy} onChange={(event) => { setSortBy(event.target.value); setPageIndex(0); }} sx={{ minWidth: 120 }}>
              <MenuItem value="createdAt">Created</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="used_at">Used</MenuItem>
            </TextField>
            <TextField select label="Direction" size="small" value={sortOrder} onChange={(event) => { setSortOrder(event.target.value); setPageIndex(0); }} sx={{ minWidth: 110 }}>
              <MenuItem value="DESC">DESC</MenuItem>
              <MenuItem value="ASC">ASC</MenuItem>
            </TextField>
          </Stack>
          <Pagination count={Math.max(totalPages, 1)} page={Math.min(pageIndex + 1, Math.max(totalPages, 1))} onChange={(_, page) => setPageIndex(page - 1)} shape="rounded" />
        </Box>
      </Paper>
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` } }}>
        <DialogTitle sx={{ color: colors.grey[100] }}>Password Reset Details</DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>Email</Typography>
              <Typography variant="body1" sx={{ color: colors.grey[100], mb: 2 }}>{selectedItem.email}</Typography>
              <Typography variant="body2" sx={{ color: colors.grey[400], mb: 0.5 }}>OTP</Typography>
              <Typography variant="body1" sx={{ color: colors.grey[100] }}>{selectedItem.otp}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} sx={{ color: colors.grey[100] }}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`, border: `1px solid ${colors.grey[800]}30` } }}>
        <DialogTitle sx={{ color: colors.grey[100] }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.grey[200] }}>Are you sure you want to delete this password reset? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: colors.grey[100] }}>Cancel</Button>
          <Button onClick={confirmDelete} sx={{ color: colors.redAccent[400] }}>Delete</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

